import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Modal, Spin } from 'antd';
import FeedbackModal from '../../components/FeedbackModal';
// scrollTriggerIntoView is used via dynamic import in ScrollOnOpen to avoid calling
// synchronously during render on older browsers; don't import it statically here.
import { useLinkCorporateAccount } from '../../hooks/dashboard/useLinkCorporateAccount';
import { useCompanySearch } from '../../hooks/dashboard/useCompanySearch';
import { useModalWidth } from '../../hooks/useModalWidth';

export default function LinkAccountModal({ visible, onClose, onSuccess }) {
  const modalWidth = useModalWidth();
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [staffId, setStaffId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showCompanyOptions, setShowCompanyOptions] = useState(false);
  const [companyValidationError, setCompanyValidationError] = useState('');
  const hideOptionsTimeout = useRef(null);

  const omkTextFieldStyles = React.useMemo(() => ({
    width: '100%',
    '--omk-textfield-height': '48px',
    '--omk-textfield-border-radius': '8px',
    '--omk-textfield-padding': '0 16px',
    '--omk-textfield-background': '#fff',
    '--omk-textfield-border-color': '#d9d9d9',
    '--omk-textfield-hover-border-color': '#00b050',
  }), []);

  const handleFormChange = (field) => (event) => {
    const value = event?.target?.value ?? event;
    if (field === 'staffId') {
      setStaffId(value);
    }
  };

  const trimmedSearchTerm = companySearchTerm.trim();
  const { data: companyResults, isFetching: searchingCompanies, error: companySearchError } = useCompanySearch(trimmedSearchTerm);

  const companyOptions = useMemo(() => (
    Array.isArray(companyResults)
      ? companyResults.map((company, index) => ({
          value: company?.company_policy_no || company?.company_name || `company-${index}`,
          label: company?.company_name || 'Unknown Company',
          policyNumber: company?.company_policy_no || '',
          raw: company
        }))
      : []
  ), [companyResults]);

  useEffect(() => {
    if (companyOptions.length > 0) {
      console.log('Company search results:', companyOptions);
    }
  }, [companyOptions]);

  useEffect(() => {
    if (selectedCompany && !companyOptions.some(option => option.value === selectedCompany)) {
      setSelectedCompany("");
    }
  }, [companyOptions, selectedCompany]);

  useEffect(() => {
    if (trimmedSearchTerm.length < 2) {
      setShowCompanyOptions(false);
    }
  }, [trimmedSearchTerm]);

  useEffect(() => {
    return () => {
      if (hideOptionsTimeout.current) {
        clearTimeout(hideOptionsTimeout.current);
      }
    };
  }, []);

  const companyStatusMessage = useMemo(() => {
    if (trimmedSearchTerm.length < 2) return 'Type at least 2 characters to search';
    if (companySearchError) return 'Unable to load companies';
    if (searchingCompanies) return 'Searching companies...';
    if (companyOptions.length === 0) return 'No companies found';
    return '';
  }, [trimmedSearchTerm, companySearchError, searchingCompanies, companyOptions.length]);

  const linkMutation = useLinkCorporateAccount();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const selected = companyOptions.find(option => option.value === selectedCompany);
      if (!selected) {
        setCompanyValidationError('Please select a company from the suggestions.');
        setSubmitting(false);
        return;
      }
      setCompanyValidationError('');
      const company_name = selected.label;
      const company_policy_no = selected.policyNumber;
      const payload = { company_name, company_policy_no, staff_id: staffId.trim() };
      console.log('Submitting link corporate account payload:', payload);
      await linkMutation.mutateAsync(payload);
      setSubmitting(false);
      // notify parent so it can refresh corporate accounts
      onSuccess && onSuccess(selectedCompany);
      // open the feedback prompt modal and keep this modal open until user dismisses
      setShowFeedbackModal(true);
    } catch (err) {
      setSubmitting(false);
      // Error notification handled by notifyUser in the mutation hook
    }
  };

  return (
    <>
    <Modal
      centered
      open={visible}
      destroyOnClose={true}
      closable={false}
      footer={null}
      onCancel={onClose}
      className="link-account-modal"
      zIndex={4000}
      width={modalWidth}
      bodyStyle={{ padding: 0 }}
    >
      <div className="link-modal-shell" style={{ padding: '32px 32px 24px 32px', width: '100%' }}>
        <div className="success-modal">
          {/* MODIFIED: Adjusted heading style for better mobile scaling */}
          <p className="lead mt-4 modal-title-header" style={{ fontWeight: 700, fontSize: 20, margin: '0 0 16px 0', textAlign: 'left' }}>Link Your Account</p>
          <div style={{ height: 16 }} />
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 12,
              right: 18,
              background: 'none',
              border: 'none',
              fontSize: 26,
              color: '#888',
              cursor: 'pointer',
              zIndex: 10
            }}
            aria-label="Close"
          >×</button>
          <form onSubmit={handleSubmit} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ width: '100%' }}>
              <div style={{ position: 'relative', width: '100%' }}>
                <omk-text-field
                  label="Company (type to search & select)"
                  value={companySearchTerm}
                  type="text"
                  name="companySearch"
                  placeholder="Start typing company name or policy number"
                  style={omkTextFieldStyles}
                  onFocus={() => setShowCompanyOptions(true)}
                  onBlur={() => {
                    hideOptionsTimeout.current = setTimeout(() => setShowCompanyOptions(false), 120);
                  }}
                  onInput={e => {
                    const nextValue = e.detail?.value ?? e.target?.value ?? '';
                    setCompanySearchTerm(nextValue);
                    setCompanyValidationError('');
                    if (selectedCompany) {
                      const matched = companyOptions.some(option => option.value === selectedCompany && option.label === nextValue);
                      if (!matched) {
                        setSelectedCompany('');
                      }
                    }
                    if (!showCompanyOptions) setShowCompanyOptions(true);
                  }}
                  required
                />
                {showCompanyOptions && trimmedSearchTerm.length >= 2 && (
                  <div
                    className="company-options-dropdown"
                    onMouseDown={e => {
                      // prevent blur before click handler runs
                      e.preventDefault();
                    }}
                  >
                    {companyStatusMessage && (
                          <div className="company-options-status">
                            {searchingCompanies ? (<><Spin size="small" style={{ marginRight: 8 }} />Searching companies...</>) : companyStatusMessage}
                          </div>
                        )}
                    {!companyStatusMessage && companyOptions.map(option => (
                      <button
                        type="button"
                        key={option.value}
                        className={`company-option ${selectedCompany === option.value ? 'active' : ''}`}
                        onClick={() => {
                          clearTimeout(hideOptionsTimeout.current);
                          setSelectedCompany(option.value);
                          setCompanySearchTerm(option.label);
                          setCompanyValidationError('');
                          setShowCompanyOptions(false);
                        }}
                      >
                        <div className="company-option-name">{option.label}</div>
                        {option.policyNumber && (
                          <div className="company-option-meta">Policy: {option.policyNumber}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="small-text-hint" style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                Enter at least 2 characters to see matching companies. Selecting an option locks it in for submission.
              </div>
              {companyValidationError && (
                <div className="error-text-small" style={{ fontSize: 12, color: '#d93025', marginTop: 6 }}>
                  {companyValidationError}
                </div>
              )}
            </div>
            <div style={{ width: '100%' }}>
              <omk-text-field
                label="Staff ID"
                value={staffId}
                type="text"
                name="staffid"
                placeholder="Enter Staff ID"
                style={omkTextFieldStyles}
                onInput={handleFormChange('staffId')}
                required
              />
            </div>
           
            <div style={{ width: '100%' }}>
              <button
                className="btn btn-primary mb-4"
                type="submit"
                disabled={submitting}
                style={{ width: '100%' }}
              >
                {submitting ? 'Linking...' : 'Apply'}
              </button>
            </div>
          </form>
       
        </div>
      </div>
    </Modal>
    <style>{`
      /* Base styles for all screens */
      .link-account-modal .ant-modal-content {
        border-radius: 20px;
        padding: 0;
      }
      /* Ensure the modal is always centered by Ant Design's implementation */
      .link-account-modal .ant-modal {
        margin: 0 auto;
      }
      .company-options-dropdown {
        position: absolute;
        top: calc(100% + 8px);
        left: 0;
        width: 100%;
        max-height: 220px;
        overflow-y: auto;
        background: #fff;
        border: 1px solid #e5e5e5;
        border-radius: 12px;
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
        z-index: 50;
        padding: 8px;
      }
      .company-options-status, .company-option-name, .company-option-meta {
          /* Added class to control size on mobile */
      }
      .company-options-status {
        font-size: 13px;
        color: #666;
        padding: 12px;
        text-align: center;
      }
      .company-option {
        width: 100%;
        border: none;
        background: #f6f7fb;
        border-radius: 10px;
        padding: 10px 14px;
        text-align: left;
        margin-bottom: 8px;
        cursor: pointer;
        transition: background 0.2s ease, transform 0.15s ease;
      }
      .company-option:last-child {
        margin-bottom: 0;
      }
      .company-option:hover {
        background: #e9f8ef;
      }
      .company-option.active {
        background: linear-gradient(90deg, #e6f9ed 0%, #b6f7d2 100%);
      }
      .company-option-name {
        font-weight: 600;
        color: #111;
      }
      .company-option-meta {
        font-size: 12px;
        color: #555;
        margin-top: 2px;
      }
      
      /* Mobile Optimizations (max-width: 640px) */
      @media (max-width: 640px) {
        .link-account-modal .ant-modal {
          /* Pull the modal up on mobile so the card appears nearer the top */
          max-width: 100vw;
          width: auto !important;
          margin: 0;
          padding: 0;
          left: 50%;
          top: 8% !important; /* pull up */
          transform: translate(-50%, 0) !important; /* avoid vertical centering */
        }
        /* Prevent modal content from being hidden behind the bottom nav: allow scrolling inside modal */
        .link-account-modal .ant-modal-content {
          max-height: calc(100vh - 120px);
          overflow-y: auto;
        }
        .link-account-modal .link-modal-shell {
          padding: 18px 16px 16px 16px !important; /* Reduced padding on all sides for mobile */
        }
        /* Ensure a little extra breathing room for very narrow screens */
        .link-account-modal .ant-modal-content {
          margin: 0 12px;
        }
        .link-account-modal form {
          gap: 16px !important; /* Reduced form element spacing */
        }
        /* Font Size Reductions */
        .modal-title-header {
            font-size: 18px !important; /* Reduced main title size */
            margin-bottom: 12px !important;
        }
        .company-options-status, .small-text-hint, .error-text-small, .company-option-meta {
            font-size: 11px !important; /* Reduced hint/error text size */
        }
        .company-option-name {
            font-size: 14px; /* Reduced company option name size */
        }
        .company-options-dropdown {
            max-height: 180px; /* Reduced dropdown height */
            top: calc(100% + 6px);
        }
        .company-option {
            padding: 8px 12px; /* Reduced option padding */
        }
      }
    `}</style>

    <FeedbackModal
      visible={showFeedbackModal}
      onClose={() => {
        // close feedback modal and then close the link modal
        setShowFeedbackModal(false);
        onClose && onClose();
      }}
    />
    </>
  );
}
